import type {Component, Editor} from 'grapesjs';
import {EmailCampaignAiRewriteSource, useGetRemainingCreditsForEmailCampaignAiContent} from '@Libraries/ai-email-campaign-content-library';
import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {AiRedesignPromptBoxProps, SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {AiButton} from '../ai-button';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {Size} from '@Components/button';
import {AiRedesignPromptBox} from '@Components/ai-resdesign-prompt-box';
import styles from './email-maker-ai-rewrite-prompt-box.module.scss';
import {Text, TextSize} from '@Components/text';
import {
  EMAIL_MAKER_AI_REWRITE_GENERATION_OPTIONS,
  EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT,
  EMAIL_MAKER_AI_REWRITE_PROMPT,
} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import {isMobileWidth} from '@Libraries/email-campaigns-library';
import {openAiRedesignPromptBoxModal} from '@Modals/ai-redesign-prompt-box-modal';
import {
  executeRewriteSelectedComponent,
  getAiRewritePromptBoxProps,
  getSelectedComponentsPromptBoxIssue,
  isSelectedComponenetsPromptBoxLoading,
  removeSelectedComponentsRewriteIssue,
} from '@Libraries/email-campaign-ai-rewrite-library';
import {AnimatePresence, motion} from 'framer-motion';

export interface EmailMakerAiRewritePromptBoxProps {
  editor: Editor;
  selectedComponent: Component;
}

export function EmailMakerAiRewritePromptBox({editor, selectedComponent}: EmailMakerAiRewritePromptBoxProps): ReactElement {
  const usageSource = EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REWRITE;
  const remainingCredits = useGetRemainingCreditsForEmailCampaignAiContent();
  const rewriteIssue = getSelectedComponentsPromptBoxIssue(selectedComponent, usageSource);
  const isLoading = isSelectedComponenetsPromptBoxLoading(selectedComponent);
  const isMobile = isMobileWidth();
  const openerRef = useRef<HTMLButtonElement>(null);
  const promptBoxRef = useRef<HTMLDivElement>(null);
  const [showPromptBox, setShowPromptBox] = useState(false);

  useEffect(() => {
    if (!rewriteIssue) {
      return;
    }

    if (isMobile) {
      openModal();
      return;
    }

    setShowPromptBox(true);
  }, [rewriteIssue]);

  useEffect(() => {
    if (!showPromptBox) {
      return;
    }

    document.addEventListener('touchstart', closePromptBoxListener);
    document.addEventListener('mousedown', closePromptBoxListener);

    return (): void => {
      document.removeEventListener('touchstart', closePromptBoxListener);
      document.removeEventListener('mousedown', closePromptBoxListener);
    };
  }, [showPromptBox]);

  useEffect(() => {
    const handleBeforeUnload = (): void => {
      sessionStorage.removeItem(EMAIL_MAKER_AI_REWRITE_GENERATION_OPTIONS);
      sessionStorage.removeItem(EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT);
      sessionStorage.removeItem(EMAIL_MAKER_AI_REWRITE_PROMPT);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const closePromptBoxListener = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    if (!promptBoxRef.current?.contains(target) && !openerRef.current?.contains(target)) {
      setShowPromptBox(false);
      if (rewriteIssue) {
        removeSelectedComponentsRewriteIssue(selectedComponent);
      }
    }
  };

  const rewriteSelectedComponent = async (prompt: string, selectedPromptOptionValues: SelectedPromptOptionValues): Promise<void> => {
    setShowPromptBox(false);

    await executeRewriteSelectedComponent(editor, selectedComponent, usageSource, rewriteIssue, prompt, selectedPromptOptionValues);
  };

  const getAiPromptBoxProps = (): AiRedesignPromptBoxProps => {
    const promptBoxClass = isMobile ? styles.mobilePromptBox : '';
    return getAiRewritePromptBoxProps(rewriteSelectedComponent, rewriteIssue, remainingCredits, promptBoxClass, editor);
  };

  const getPromptBox = (): ReactElement | undefined => {
    if (isMobile || !showPromptBox) {
      return undefined;
    }

    return (
      <motion.div
        initial={{opacity: 0, height: 0, y: -8}}
        animate={{
          opacity: 1,
          height: 'auto',
          y: 0,
          overflow: 'hidden',
          transitionEnd: {overflow: 'visible'},
        }}
        transition={{duration: 0.2, ease: 'easeInOut'}}
        exit={{opacity: 0, height: 0, y: -8, overflow: 'hidden'}}
      >
        <div ref={promptBoxRef}>
          <AiRedesignPromptBox {...getAiPromptBoxProps()} />
        </div>
      </motion.div>
    );
  };

  const openModal = (): void => {
    openAiRedesignPromptBoxModal({
      title: window.i18next.t('pmwjs_ai_rewrite'),
      icon: 'icon-ai-caption',
      onClose: () => {
        if (rewriteIssue) {
          removeSelectedComponentsRewriteIssue(selectedComponent);
        }
      },
      ...getAiPromptBoxProps(),
    });
  };

  const handleClick = (): void => {
    if (rewriteIssue) {
      removeSelectedComponentsRewriteIssue(selectedComponent);
    }

    if (isMobile) {
      openModal();
      return;
    }

    setShowPromptBox(!showPromptBox);
  };

  const getPromptBoxToggler = (): ReactElement => {
    return (
      <div className={'flexbox flex-align-items-center flex-justify-between'}>
        <div className={'flexbox flex-align-items-center gap-2'}>
          <Text val={window.i18next.t('pmwjs_ai_rewrite')} size={TextSize.XSMALL} className={`content-ai`} />
          <PulsatingDot containerClassname={`_position-relative  ${styles.pulsatingDot}`} />
        </div>
        <AiButton
          variant={AIButtonVariant.SUBDUED}
          size={Size.MEDIUM}
          icon={'icon-ai-caption'}
          onClick={handleClick}
          isLoading={isLoading}
          buttonRef={openerRef}
          customClasses={`${styles.headerButton} ${showPromptBox ? styles.selectedButtonBackground : styles.unselectedButtonBackground}`}
        />
      </div>
    );
  };

  return (
    <div className={'flexbox flex-column gap-2'}>
      {getPromptBoxToggler()}
      <AnimatePresence initial={false}>{getPromptBox()}</AnimatePresence>
    </div>
  );
}
